/**
 * DashboardSummaryStrip — compact hero strip at the top of /dashboard.
 *
 * Shows three at-a-glance stats for the parent / member account:
 *   1. Total sessions remaining across all active packs (across all kids)
 *   2. Active subscription (if any) — shows tier name + how many weeks left
 *   3. Next scheduled session — date/time/athlete
 *
 * Stays out of the way: if a parent has none of these, returns null.
 * Loads in parallel with the rest of the dashboard, so no blocking on render.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Ticket, Calendar, Award, Loader2, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function fmtBookingDate(dateStr) {
    // dateStr is "YYYY-MM-DD" — render as "Tue, May 21"
    if (!dateStr) return "—";
    const [y, m, d] = dateStr.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return `${DAY_LABELS[dt.getDay()]}, ${dt.toLocaleString("en-US", { month: "short", day: "numeric" })}`;
}

function fmtTime(t) {
    // t is "HH:MM" — render as "4:30 PM"
    if (!t) return "";
    const [h, m] = t.split(":").map(Number);
    const period = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

export default function DashboardSummaryStrip() {
    const [busy, setBusy] = useState(true);
    const [credits, setCredits] = useState(null);
    const [commitments, setCommitments] = useState([]);
    const [bookings, setBookings] = useState([]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [c, cm, b] = await Promise.all([
                    axios.get(`${API}/billing/me/credits`, { withCredentials: true }).catch(() => ({ data: null })),
                    axios.get(`${API}/me/commitments`, { withCredentials: true }).catch(() => ({ data: { commitments: [] } })),
                    axios.get(`${API}/schedule/my-bookings`, { withCredentials: true }).catch(() => ({ data: [] })),
                ]);
                if (cancelled) return;
                setCredits(c.data);
                setCommitments(cm.data?.commitments || []);
                const list = Array.isArray(b.data) ? b.data : (b.data?.bookings || []);
                setBookings(list);
            } finally {
                if (!cancelled) setBusy(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (busy) {
        return (
            <div data-testid="dashboard-summary-loading" className="flex items-center gap-2 text-zinc-400 text-xs py-3">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading summary…
            </div>
        );
    }

    const sessionsLeft = credits?.total_remaining ?? 0;
    const activeSub = commitments.find((c) => c.status === "active" || c.status === "pending");
    // Next-soonest booking
    const upcoming = bookings
        .filter((b) => b.status === "booked")
        .sort((a, b) => {
            const ax = `${a.date}T${a.start_time}`;
            const bx = `${b.date}T${b.start_time}`;
            return ax.localeCompare(bx);
        });
    const next = upcoming[0];

    // If they have absolutely nothing relevant, hide entirely.
    if (sessionsLeft === 0 && !activeSub && !next) return null;

    return (
        <div data-testid="dashboard-summary-strip" className="mb-8 grid sm:grid-cols-3 gap-3">
            {/* Sessions remaining */}
            <SummaryCell
                testid="summary-sessions-left"
                icon={Ticket}
                accent="emerald"
                label="Sessions remaining"
                value={sessionsLeft > 0 ? sessionsLeft : "—"}
                sub={sessionsLeft > 0 ? `${credits?.active_count || 0} pack${(credits?.active_count || 0) === 1 ? "" : "s"} active` : "No active packs"}
                to="/dashboard/billing"
            />
            {/* Active subscription */}
            <SummaryCell
                testid="summary-active-sub"
                icon={Award}
                accent="amber"
                label="Membership"
                value={activeSub ? (activeSub.display_name?.split(" · ")[1] || activeSub.display_name || "Active") : "None"}
                sub={activeSub ? (
                    activeSub.status === "pending" ? "Starts on first check-in" :
                    activeSub.commitment_ends_at ? `Ends ${fmtBookingDate(activeSub.commitment_ends_at.slice(0, 10))}` : "Active"
                ) : "Not enrolled"}
                to="/dashboard/billing"
            />
            {/* Next session */}
            <SummaryCell
                testid="summary-next-session"
                icon={Calendar}
                accent="red"
                label="Next session"
                value={next ? fmtTime(next.start_time) : "—"}
                sub={next ? `${fmtBookingDate(next.date)}${next.athlete_name ? ` · ${next.athlete_name}` : ""}` : "Nothing scheduled"}
                to="/dashboard/schedule"
            />
        </div>
    );
}

const ACCENT_STYLES = {
    emerald: { border: "border-emerald-900/40", bg: "bg-emerald-950/10", icon: "text-emerald-400" },
    amber:   { border: "border-amber-900/40",   bg: "bg-amber-950/10",   icon: "text-amber-300" },
    red:     { border: "border-brand-red/40",   bg: "bg-brand-red/10",   icon: "text-brand-red" },
};

function SummaryCell({ testid, icon: Icon, accent, label, value, sub, to }) {
    const style = ACCENT_STYLES[accent] || ACCENT_STYLES.red;
    const Inner = (
        <div className={`border ${style.border} ${style.bg} rounded-2xl px-4 py-3 transition-colors hover:bg-zinc-950/50`}>
            <div className="flex items-center justify-between gap-2 text-[10px] tracking-[0.22em] uppercase text-zinc-400">
                <div className="inline-flex items-center gap-1.5">
                    <Icon className={`w-3 h-3 ${style.icon}`} />
                    <span>{label}</span>
                </div>
                {to && <ChevronRight className="w-3 h-3 text-zinc-500" />}
            </div>
            <div className="mt-1 text-2xl font-display tracking-wider text-white truncate">{value}</div>
            <div className="text-[11px] text-zinc-400 mt-0.5 truncate">{sub}</div>
        </div>
    );
    if (to) {
        return <Link to={to} data-testid={testid} className="block">{Inner}</Link>;
    }
    return <div data-testid={testid}>{Inner}</div>;
}
